/**
 * Venue map + list interactions (Leaflet + OSM). Progressive enhancement:
 * runs only if a .venue-map[data-map] container is present. Cards/list work
 * without this script.
 */
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../blocks/venue-catalog/style.css';
import '../blocks/venue-profile/style.css';

function pinIcon() {
	return L.divIcon({
		className: 'venue-pin',
		html: '<span class="venue-pin-dot"></span>',
		iconSize: [22, 22],
		iconAnchor: [11, 22],
	});
}

function initMap(container) {
	const dataEl = container.parentElement.querySelector('.venue-map-data');
	let points = [];
	try { points = JSON.parse(dataEl?.textContent || '[]'); } catch (e) { points = []; }

	const map = L.map(container, { scrollWheelZoom: false });
	L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
		maxZoom: 19,
		attribution: '&copy; OpenStreetMap contributors',
	}).addTo(map);

	const markers = {};
	const latlngs = [];
	points.forEach((p) => {
		const m = L.marker([p.lat, p.lng], { icon: pinIcon(), title: p.name }).addTo(map);
		const popupEl = document.createElement('div');
		const popupName = document.createElement('strong');
		popupName.textContent = p.name;
		const popupLink = document.createElement('a');
		popupLink.href = p.url;
		popupLink.textContent = 'Katso paikka →';
		popupEl.append(popupName, document.createElement('br'), popupLink);
		m.bindPopup(popupEl);
		markers[p.id] = m;
		latlngs.push([p.lat, p.lng]);
		const card = document.querySelector(`.venue-card[data-venue-id="${p.id}"]`);
		if (card) {
			m.on('click', () => card.scrollIntoView({ behavior: 'smooth', block: 'center' }));
		}
	});

	if (latlngs.length > 1) {
		map.fitBounds(latlngs, { padding: [30, 30] });
	} else if (latlngs.length === 1) {
		map.setView(latlngs[0], 14);
	} else {
		map.setView([61.5, 24.5], 5); // Finland fallback.
	}

	return { map, markers };
}

function initCatalog(root, map, markers) {
	const cards = Array.from(root.querySelectorAll('.venue-card'));
	const typeSel = root.querySelector('.venue-filter-type');
	const citySel = root.querySelector('.venue-filter-city');
	const search = root.querySelector('.venue-search');
	const empty = root.querySelector('.venue-empty');

	// Hover a card -> highlight its pin.
	cards.forEach((card) => {
		const id = card.getAttribute('data-venue-id');
		card.addEventListener('mouseenter', () => markers[id]?.openPopup());
		card.addEventListener('mouseleave', () => markers[id]?.closePopup());
	});

	function apply() {
		const t = typeSel?.value || '';
		const c = citySel?.value || '';
		const q = (search?.value || '').trim().toLowerCase();
		let visible = 0;
		cards.forEach((card) => {
			const okType = !t || card.getAttribute('data-type') === t;
			const okCity = !c || (card.getAttribute('data-cities') || '').split('|').includes(c);
			const okText = !q || (card.getAttribute('data-name') || '').includes(q);
			const show = okType && okCity && okText;
			card.hidden = !show;
			const id = card.getAttribute('data-venue-id');
			if (markers[id]) {
				if (show) { markers[id].addTo(map); } else { map.removeLayer(markers[id]); }
			}
			if (show) visible++;
		});
		if (empty) empty.hidden = visible !== 0;
	}

	[typeSel, citySel].forEach((el) => el && el.addEventListener('change', apply));
	if (search) search.addEventListener('input', apply);
}

export function initVenueMaps() {
	document.querySelectorAll('.venue-map[data-map]').forEach((container) => {
		const { map, markers } = initMap(container);
		const root = container.closest('.venue-catalog');
		if (root) initCatalog(root, map, markers);
		// Leaflet needs a resize nudge inside sticky/flex layouts.
		setTimeout(() => map.invalidateSize(), 200);
	});
}

if (document.readyState !== 'loading') {
	initVenueMaps();
} else {
	document.addEventListener('DOMContentLoaded', initVenueMaps);
}
